import {
  Card,
  createTheme,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { GridOff } from '@material-ui/icons';
import RefreshIcon from '@material-ui/icons/Autorenew';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import { withStyles } from '@material-ui/core/styles';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { initialize } from 'redux-form';
import { BLUE, CUSTOM_TABLE_FIELD_TYPES, TABLE_HEADER_COLOR } from '../../_config/consts.js';
import { getColorFromThemeName } from '../../_config/utils';
import CustomTableFilterHeader from '../../components/CustomTables/CustomTableFilterHeader';
import EmptyView from '../../components/EmptyView.jsx';
import CustomTableRowForm from '../../components/forms/CustomTables/CustomTableRowForm.jsx';
import ListLoadingView from '../../components/ListLoadingView.jsx';
import MDTablePaginationActions from '../../components/MDTable/MDTablePaginationActions';
import OperationalView from '../../components/OperationalView/OperationalView';
import * as CustomTablesActions from '../../redux/actions/customTables.actions';
import * as ModalActions from '../../redux/actions/modal.actions';
import * as UtilsActions from '../../redux/actions/utils.actions';


const theme = createTheme({
  palette: {
    primary: { 500: BLUE },
  },
});

const styles = theme => ({
  table: {
    overflow: 'scroll',
  },
  tableColumnText: {
    fontWeight: 'bold',
    color: '#3f3f3f',
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
    minHeight: 80,
  },
  tableCell: {
    padding: '12px 16px',
  },
  rowSelected: {
    backgroundColor: 'aliceblue !important',
    color: 'aliceblue !important',
  },
  rowHovered: {
    cursor: 'pointer',
  },
  paginationText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#3f3f3f',
  },
  disabledInput: {
    display: 'none',
  },
  button: {
    color: 'red',
    fontSize: 13,
    fontWeight: 'bold',
    textDecoration: 'underline',
    marginLeft: -15,
    width: 'max-content',
  },
});

@connect(state => ({
  selectedCustomTable: state.customTables.selectedCustomTable,
  customTableRows: state.customTables.rows,
  themeName: state.settings.items.theme.data.themeName 
}))
class CustomTableDisplayView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loadingData: false,
      showRowForm: false,
      selectedRow: null,
      filters: {},
    };
  }

  async componentDidMount() {
    const tableId = window.location.pathname.split('/').pop();
    this.fetchTable(tableId);
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.location.state.customTable.id !== this.props.location.state.customTable.id)
      this.fetchTable(this.props.location.state.customTable.id);
  }

  componentWillUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  }

  hasActiveFilters = () => {
    const { filters } = this.state;
    if (!filters || Object.keys(filters).length === 0) return false;
    
    return Object.values(filters).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      } else if (typeof value === 'object' && value !== null) {
        return (value.min && value.min.toString().trim()) || (value.max && value.max.toString().trim());
      }
      return false;
    });
  }

  async fetchTable(tableId) {
    const { dispatch } = this.props;
    this.setState({ loading: true, filters: {} });
    try {
      dispatch(CustomTablesActions.resetCustomTablesPagination());
      dispatch(CustomTablesActions.resetCustomTableRowsData());
      const customTableDetails = await dispatch(CustomTablesActions.getCustomTableDetails(tableId));
      await dispatch(CustomTablesActions.setSelectedCustomTable(customTableDetails));
      await this.fetchTableData(tableId);
    } finally {
      this.setState({ loading: false });
    }
  }

  async fetchTableData(tableId) {
    const { dispatch, selectedCustomTable } = this.props;
    const { filters } = this.state;
    if (!tableId) return;
    this.setState({ loadingData: true });
    try {
      await dispatch(CustomTablesActions.fetchCustomTableRows(tableId, 0, 50, filters, selectedCustomTable));
    } finally {
      this.setState({ loadingData: false });
    }
  }

  renderTableHeaders() {
    const { selectedCustomTable, classes } = this.props;
    if (!selectedCustomTable || !selectedCustomTable.fields) return null;
    if (selectedCustomTable && (!selectedCustomTable.fields || selectedCustomTable.fields.length === 0)) {
      return null;
    }
    return (
      <>
        {_.map(selectedCustomTable.fields, (field) => (
          <TableCell key={field.id} className={classes.tableCell}>
            <h4 className={classes.tableColumnText}>{field.name}</h4>
          </TableCell>
        ))}
        <TableCell className={classes.tableCell}><h4 className={classes.tableColumnText}><Entity entity="actions" /></h4></TableCell>
      </>
    );
  }

  shouldShowEmptyView() {
    const { selectedCustomTable, customTableRows } = this.props;
    const { loadingData } = this.state;
    
    if (loadingData) return false;
    
    if (selectedCustomTable && (!selectedCustomTable.fields || selectedCustomTable.fields.length === 0))
      return true;
    
    if (_.isEmpty(customTableRows.content))
      return true;
    
    return false;
  }

  renderCellValue(field, row) {
    const fieldValue = row && row.data && row.data[field.name];
    
    if (field.type === CUSTOM_TABLE_FIELD_TYPES.EXTERNAL_REFERENCE) {
      if (!fieldValue) {
        return '---';
      }
      
      if (typeof fieldValue === 'object' && fieldValue.displayName) {
        const isNotFound = fieldValue.entityData === null;
        return (
          <span style={{ 
            color: isNotFound ? '#f57c00' : 'inherit',
            fontWeight: isNotFound ? 'bold' : 'normal'
          }}>
            {fieldValue.displayName}
          </span>
        );
      }
      
      return fieldValue;
    }
    
    return fieldValue ? fieldValue : '---';
  }

  renderTableBody() {
    const { selectedCustomTable, customTableRows, classes } = this.props;

    return (
      <TableBody>
        {_.map(customTableRows.content, (row, index) => (
          <TableRow 
            key={index}
            className={classes.row}
            hover
            classes={{
              hover: classes.rowHovered,
            }}
            onClick={() => this.onEditRow(row)}
          >
            {_.map(selectedCustomTable.fields, (field) => (
              <TableCell key={field.id} className={classes.tableCell}>
                <div style={{ display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
                  <h5 style={{ lineHeight: '1.5', fontSize: 14, marginTop: 10 }}>
                    {this.renderCellValue(field, row)}
                  </h5>
                </div>
              </TableCell>
            ))}
            <TableCell>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    this.onDeleteRow(row.id);
                  }}
                  className={classes.button}
                >
                  <Entity entity="delete" />
                  <DeleteIcon style={{ fontSize: 15, marginLeft: 5 }} />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  }

  onRefreshTable() {
    if (!this.props.selectedCustomTable || !this.props.selectedCustomTable.id) return;
    this.fetchTableData(this.props.selectedCustomTable.id);
  }

  onFetchTableOnPage(page) {
    // TODO
  }

  onNewRow(){
    this.setState({ showRowForm: true, selectedRow: null });
  }

  async onEditRow(row) {
    const { dispatch, selectedCustomTable } = this.props;
    try {
      const formData = {};
      const rowData = (row || {}).data || {};
      
      _.forEach(selectedCustomTable.fields, (field) => {
        const fieldValue = rowData[field.name];
        
        if (field.type === CUSTOM_TABLE_FIELD_TYPES.EXTERNAL_REFERENCE && typeof fieldValue === 'object' && fieldValue.id) {
          formData[field.name] = { value: fieldValue.id, label: fieldValue.displayName };
        } else {
          formData[field.name] = fieldValue;
        }
      });
      
      dispatch(initialize('CustomTableRowForm', formData));
      this.setState({ showRowForm: true, selectedRow: row });
    } catch (error) {
      this.setState({ showRowForm: false, selectedRow: null });
    }
  }

  onCloseForm() {
    this.setState({ showRowForm: false, selectedRow: null });
  }

  onDeleteRow(rowId) {
    const { dispatch, selectedCustomTable } = this.props;
    
    dispatch(ModalActions.showModal({
      modalType: 'CONFIRM_TO_CONTINUE_MODAL',
      modalProps: {
        title: 'confirmBeforeContinue',
        body: <Entity entity="deleteCustomTableRowConfirmation" />,
        onConfirmText: <Entity entity="yes" />,
        onConfirm: () => this.onDeleteRowConfirmed(rowId),
        onCancelText: <Entity entity="no" />,
        onCancel: () => dispatch(ModalActions.hideModal()),
      },
    }));
  }

  async onDeleteRowConfirmed(rowId) {
    const { dispatch, selectedCustomTable } = this.props;
    try {
      dispatch(ModalActions.hideModal());
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await dispatch(CustomTablesActions.deleteCustomTableRow(selectedCustomTable.id, rowId));
      dispatch(UtilsActions.setSpinnerVisibile(false));
      this.fetchTableData(selectedCustomTable.id);
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="customTableRowActionError" /></h6>),
        }
      }));
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onSubmitRow = async (fieldValues) => {
    const { dispatch, selectedCustomTable } = this.props;
    const { selectedRow } = this.state;
    try {
      const dataParsed = {
        ...fieldValues,
        data: undefined,
        id: undefined,
      }
      dispatch(UtilsActions.setSpinnerVisibile(true));
      if (selectedCustomTable && selectedCustomTable.fields) {        
        selectedCustomTable.fields.forEach(field => {
          if (field.type === CUSTOM_TABLE_FIELD_TYPES.INTEGER_NUMBER) {
            dataParsed[field.name] = _.toNumber(dataParsed[field.name]) || 0;
            dataParsed[field.name] = _.round(dataParsed[field.name], 0);
          }
          if (field.type === CUSTOM_TABLE_FIELD_TYPES.DATE && dataParsed[field.name]) {
            const dateValue = dataParsed[field.name];            
            if (dateValue && typeof dateValue === 'object' && dateValue._isAMomentObject) {
              dataParsed[field.name] = dateValue.format('YYYY-MM-DD');
            }
            else if (dateValue instanceof Date) {
              dataParsed[field.name] = dateValue.toISOString().split('T')[0];
            } 
            else if (typeof dateValue === 'string' && dateValue.includes('T')) {
              dataParsed[field.name] = dateValue.split('T')[0];
            }
          }
        });
      }

      if (selectedRow) {
        await dispatch(CustomTablesActions.updateCustomTableRow(selectedCustomTable.id, selectedRow.id, {data: dataParsed}));
      } 
      else {
        await dispatch(CustomTablesActions.createCustomTableRow(selectedCustomTable.id, dataParsed));
      }
      dispatch(UtilsActions.setSpinnerVisibile(false));
      this.onCloseForm();
      this.fetchTableData(selectedCustomTable.id);
    } catch (error) {
      console.error('Error submitting row:', error);
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="customTableRowActionError" /></h6>),
        },
      }));
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onExportElements(format) {
    const { dispatch, selectedCustomTable } = this.props;
    const { filters } = this.state;
    
    if (!selectedCustomTable || !selectedCustomTable.id) {
      console.warn('No custom table selected for export');
      return;
    }
    
    dispatch(CustomTablesActions.exportCustomTableRows(
      selectedCustomTable.id,
      selectedCustomTable,
      filters,
      format
    ));
  }

  onFilterChange = (fieldName, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [fieldName]: value,
      }
    }), () => {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    });
  }

  applyFilters = () => {
    const { selectedCustomTable } = this.props;
    if (selectedCustomTable && selectedCustomTable.id) {
      this.fetchTableData(selectedCustomTable.id);
    }
  }

  onFiltersReset = () => {
    this.setState({ filters: {} }, () => {
      this.applyFilters();
    });
  }

  onFiltersSubmit = () => {
    this.onRefreshTable();
  }

  render() {
    const { classes, themeName, selectedCustomTable, customTableRows: { pagination: { currentPage, totalPages, size, totalElements }}, dispatch } = this.props;
    const { loading, showRowForm, selectedRow, filters } = this.state;
    const headerColor = getColorFromThemeName(themeName);
    
    if (!selectedCustomTable || !selectedCustomTable.id) {
      return (
        <div style={{ marginTop: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <GridOff style={{ fontSize: 50, color: '#a5a3a3' }} />
          <h4 style={{ color: '#a5a3a3'}}><Entity entity="pleaseSelectCustomTable" /></h4>
        </div>
      );
    }

    const searchableFields = selectedCustomTable.fields ? selectedCustomTable.fields.filter(field => field.isSearchField) : [];
    const showRefresh = selectedCustomTable && selectedCustomTable.fields && selectedCustomTable.fields.length > 0;
    
    return (
      <div className="list-view details-section" style={{ top: 150, paddingBottom: 140, width: '87%' }}>
        <CustomTableFilterHeader
          themeName={themeName}
          searchableFields={searchableFields}
          filters={filters}
          onFilterChange={this.onFilterChange}
          onFiltersReset={this.onFiltersReset}
          onNewEntity={selectedCustomTable?.fields?.length === 0 ? null : () => this.onNewRow()}
          onRefresh={selectedCustomTable?.fields?.length === 0 ? null : () => this.onRefreshTable()}
          onExportElements={selectedCustomTable?.fields?.length === 0 ? null : format => this.onExportElements(format)}
          numberOfElements={totalElements}
          loading={loading}
        />
        {loading ? <ListLoadingView /> : (
          this.shouldShowEmptyView() ? (
            <div>
              {!loading ? (
                <EmptyView
                  title={this.hasActiveFilters() ? 'noCustomTableRowsFound' : (selectedCustomTable?.fields?.length === 0 ? 'customTableNoFields' : 'noCustomTableRows')}
                  subtitle={this.hasActiveFilters() ? 'noCustomTableRowsFoundSearchDescription' : (selectedCustomTable?.fields?.length === 0 ? 'customTableNoFieldsDescription' : 'noCustomTableRowsDescription')}
                  onNewEntity={selectedCustomTable?.fields?.length > 0 && !this.hasActiveFilters() ? () => this.onNewRow() : null}
                  newEntityTitle="addNewCustomTableRow"
                  iconName="icon-simple-line-icons-grid"
                />
              ) : <ListLoadingView />}
            </div>
          ) : (
            <Card style={{ overflow: 'scroll' }} elevation={4}>
              <TableContainer>
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: TABLE_HEADER_COLOR }}>
                      <TableCell colSpan={3}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <h3 className={classes.tableColumnText} style={{ color: '#3f3f3f', marginTop: 15, marginBottom: 15 }}>{selectedCustomTable.name}</h3>
                          {showRefresh && (
                            <IconButton onClick={() => this.onRefreshTable()} style={{ marginLeft: 10, marginTop: 5 }}>
                              <RefreshIcon style={{ color: '#3f3f3f', fontSize: 25 }} />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    <TablePagination
                      colSpan={0}
                      count={!_.isUndefined(totalElements) ? totalElements : 0}
                      rowsPerPage={!_.isUndefined(size) ? size : 0}
                      page={!_.isUndefined(currentPage) ? currentPage : 0}
                      labelRowsPerPage=" "
                      classes={{
                        input: classes.disabledInput,
                      }}
                      labelDisplayedRows={({ from, to, count }) => (
                        <h4 className={classes.paginationText}>
                          <Entity
                            key={`${from}-${totalPages}-${count}-${selectedCustomTable.name}`}
                            entity="customRowsTablePagination"
                            data={{
                              tableName: selectedCustomTable.name,
                              currentPage: !_.isUndefined(currentPage) ? currentPage + 1 : 0,
                              totalPages: !_.isUndefined(totalPages) ? totalPages : 0,
                              count: !_.isUndefined(count) ? count : 0,
                            }}
                          />
                        </h4>
                      )}
                      onChangePage={(event, page) => this.onFetchTableOnPage(page)}
                      ActionsComponent={() => (
                        <MDTablePaginationActions
                          count={totalElements}
                          iconColor="#3f3f3f"
                          page={currentPage}
                          lastPage={totalPages - 1}
                          rowsPerPage={size}
                          onChangePage={(event, page) => this.onFetchTableOnPage(page)}
                        />
                      )}
                    />
                  </TableRow>
                  <TableRow style={{ backgroundColor: 'white' }}>
                    {this.renderTableHeaders()}
                  </TableRow>
                </TableHead>
                {this.renderTableBody()}
                <TableFooter>
                  <TableRow style={{ backgroundColor: TABLE_HEADER_COLOR }}>
                    <TablePagination
                      colSpan={0}
                      count={!_.isUndefined(totalElements) ? totalElements : 0}
                      rowsPerPage={!_.isUndefined(size) ? size : 0}
                      page={!_.isUndefined(currentPage) ? currentPage : 0}
                      labelRowsPerPage=" "
                      classes={{
                        input: classes.disabledInput,
                      }}
                      labelDisplayedRows={({ from, to, count }) => (
                        <h4 className={classes.paginationText}>
                          <Entity
                            key={`${from}-${totalPages}-${count}-${selectedCustomTable.name}`}
                            entity="customRowsTablePagination"
                            data={{
                              tableName: selectedCustomTable.name,
                              currentPage: !_.isUndefined(currentPage) ? currentPage + 1 : 0,
                              totalPages: !_.isUndefined(totalPages) ? totalPages : 0,
                              count: !_.isUndefined(count) ? count : 0,
                            }}
                          />
                        </h4>
                      )}
                      onChangePage={(event, page) => this.onFetchTableOnPage(page)}
                      ActionsComponent={() => (
                        <MDTablePaginationActions
                          count={totalElements}
                          iconColor="#3f3f3f"
                          page={currentPage}
                          lastPage={totalPages - 1}
                          rowsPerPage={size}
                          onChangePage={(event, page) => this.onFetchTableOnPage(page)}
                        />
                      )}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
          )
        )}
        <OperationalView
          themeName={themeName}
          isVisible={showRowForm}
          onClose={() => this.onCloseForm()}
          title={<Entity entity={selectedRow ? 'editCustomTableRow' : 'addNewCustomTableRow'} key={selectedRow ? 'edit' : 'add'} />}
        >
          <CustomTableRowForm 
            fields={selectedCustomTable?.fields || []}
            onSubmit={(fieldValues) => this.onSubmitRow(fieldValues)}
            initialValues={selectedRow}
          />
        </OperationalView>
      </div>
    );
  }
}

export default withStyles(styles)(CustomTableDisplayView); 